import { Chip, Typography } from '@mui/material';
import {
  GridAlignment,
  GridColumnOrderChangeParams,
} from '@mui/x-data-grid-pro';
import { useContext, useEffect, useMemo, useState } from 'react';

import useFundFilter from '../../../components/Selectors/FundsFilter/useFundFilter.hook';
import { AppContext } from '../../../core/context/appContextProvider';
import { getArkAccountNames } from '../../../services/arkGL.service';
import { getBankRules, getBanks } from '../../../services/bank.service';
import {
  getColumnOrder,
  saveColumnOrder,
} from '../../../services/columnOrder.service';
import {
  arrayIndexUpdate,
  arrayVisibilityUpdate,
} from '../../../utils/helpers/columnOrder.helper';
import { DateTimeFormat } from '../../../utils/helpers/format.helper';
import { useEffectAsync } from '../../../utils/hooks/useEffectAsync.hook';
import { BankRule } from '../../../utils/types/bank.type';
import { ColumnOrder } from '../../../utils/types/columnOrder';
import { CustomType, DataGridColDef } from '../../../utils/types/listItems';
import {
  AddNewButtonOptions,
  BANK_FEEDS_VIEW_KEY,
  BankFeedsFilter,
  BankFeedsStatusFilter,
  BankFeedsTransStatus,
  BankFeedTransactionType,
  BankFeedView,
  BankFeedViewType,
  defaultHeaderList,
} from './bankFeedsList.defaults';

export const useBankFeedsList = () => {
  const { state, informationAlert } = useContext(AppContext);
  const clientId = state.loginUser.clientId;
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [viewState, setViewState] = useState<BankFeedViewType>(
    BankFeedView.HYBRID
  );
  const [headerList, setHeaderList] =
    useState<Array<DataGridColDef>>(defaultHeaderList);
  const [activeHeaderFields, setActiveHeaderFields] = useState(
    defaultHeaderList.length - 1
  );
  const [bankFeedsColumnOrder, setBankFeedsColumnOrder] =
    useState<ColumnOrder | null>(null);
  const [bankFeedsSelectionModel, setBankFeedsSelectionModel] = useState<
    string[]
  >([]);
  const [showBankConnectionPanel, setShowBankConnectionPanel] =
    useState<boolean>(false);

  const [selectedBankTransaction, setSelectedBankTransaction] =
    // useState<BankFeedTransactionType>(); //Replace
    useState<any>();

  const [bankRulesResponse, setBankRulesResponse] = useState<BankRule[]>([]);
  const [bankAccountsResponse, setBankAccountsResponse] = useState<any[]>([]); //add type
  const [accountNamesResponse, setAccountNamesResponse] = useState<any[]>([]); //add type

  const [fundFilterList, setFundFilterList] = useState<any[]>([]);

  useEffect(() => {
    initializeHeaderList();
  }, []);

  const { fundsList } = useFundFilter();

  useEffect(() => {
    if (fundsList) {
      setFundFilterList(fundsList);
    }
  }, [fundsList]);

  const fetchAllBankAccounts = async (isCanceled?: () => boolean) => {
    try {
      const allBanksResponse = await getBanks();

      if (isCanceled?.()) return;

      setBankAccountsResponse(allBanksResponse.items);
    } catch (e) {
      informationAlert('Error getting bank list', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchAllBankRules = async (isCanceled?: () => boolean) => {
    try {
      const allBankRulesResponse = await getBankRules();

      if (isCanceled?.()) return;

      setBankRulesResponse(allBankRulesResponse);
    } catch (e) {
      informationAlert('Error getting bank rules', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchAllAccountNames = async (isCanceled?: () => boolean) => {
    try {
      const allAccountNamesResponse = await getArkAccountNames();

      if (isCanceled?.()) return;

      setAccountNamesResponse(allAccountNamesResponse || null);
    } catch (e) {
      informationAlert('Error getting accounts list', 'error');
    }
  };

  const fetchColumnOrder = async (isCanceled?: () => boolean) => {
    try {
      const columnOrderResponse = await getColumnOrder(
        BANK_FEEDS_VIEW_KEY,
        clientId
      );

      if (isCanceled?.()) return;

      setBankFeedsColumnOrder(columnOrderResponse || null);
    } catch (e) {
      informationAlert('Error getting column order', 'error');
    }
  };

  useEffectAsync(async (isCanceled) => {
    // setIsLoading(true);
    // await fetchAllBankAccounts(isCanceled);
    // await fetchAllBankRules(isCanceled);
    // await fetchAllAccountNames(isCanceled);
    // await fetchColumnOrder(isCanceled);
  }, []);

  useEffect(() => {
    initializeHeaderList();
  }, [bankFeedsColumnOrder, fundFilterList]);

  const selectedFundsList = useMemo(
    () => fundsList?.map((item) => item.id),
    [fundsList]
  );

  const selectedStatusList = useMemo(
    () => BankFeedsStatusFilter?.map((item) => item.id),
    [BankFeedsStatusFilter]
  );

  const initializeHeaderList = () => {
    const updatedHeaders: DataGridColDef[] = [
      {
        field: 'bankAccount',
        headerName: 'Bank Account',
        hide: false,
        index: 1,
        width: 250,
        type: 'string',
        sortable: true,
        inlineFilter: true,
        inlineFilterName: BankFeedsFilter.BankAccount,
        inlineFilterIDField: 'id',
        inlineFilterLabelField: 'name',
        // inlineFilterOptions: bankAccountFilterList,
        // inlineFilterSelected: selectedBankAccountsList,
        emptySelectionOnClear: false,
      },
      {
        field: 'fund',
        headerName: 'Fund',
        hide: false,
        index: 2,
        width: 150,
        type: 'string',
        sortable: true,
        inlineFilter: true,
        inlineFilterName: BankFeedsFilter.Fund,
        inlineFilterIDField: 'id',
        inlineFilterLabelField: 'name',
        inlineFilterOptions: fundFilterList,
        inlineFilterSelected: selectedFundsList,
        emptySelectionOnClear: false,
        renderCell: ({ value }) => {
          return (
            <Chip
              sx={{
                '& .MuiChip-label': {
                  fontSize: '1rem',
                  lineHeight: '1.5',
                },
              }}
              label={value}
            />
          );
        },
      },
      {
        field: 'status',
        headerName: 'Status',
        hide: false,
        index: 3,
        width: 150,
        type: 'string',
        sortable: true,
        inlineFilter: true,
        inlineFilterName: BankFeedsFilter.Status,
        inlineFilterIDField: 'id',
        inlineFilterLabelField: 'name',
        inlineFilterOptions: BankFeedsStatusFilter,
        inlineFilterSelected: selectedStatusList,
        emptySelectionOnClear: false,
        renderCell: ({ value }) => {
          const val = value as string;
          const color: string =
            val === BankFeedsTransStatus.CATEGORIZED
              ? '#DEF2E4'
              : val === BankFeedsTransStatus.PENDING
              ? '#F4EFE3'
              : '#f9dcdc';

          return (
            <Chip
              sx={{
                '& .MuiChip-label': {
                  fontSize: '1rem',
                  lineHeight: '1.5',
                },
                backgroundColor: color,
              }}
              label={value}
            />
          );
        },
      },
      {
        field: 'date',
        headerName: 'Date',
        hide: false,
        index: 4,
        width: 150,
        type: 'string',
        sortable: true,
        renderCell: ({ value }) => {
          return <Typography>{DateTimeFormat.shortDate(value)}</Typography>;
        },
      },
      {
        field: 'inflow',
        headerName: 'Inflow',
        hide: false,
        index: 5,
        width: 150,
        type: 'number',
        sortable: true,
        customType: CustomType.Currency,
        currencyCodeField: 'currencyCode',
        align: 'right' as GridAlignment,
      },
      {
        field: 'outflow',
        headerName: 'Outflow',
        hide: false,
        index: 6,
        width: 150,
        type: 'number',
        sortable: true,
        customType: CustomType.Currency,
        currencyCodeField: 'currencyCode',
        align: 'right' as GridAlignment,
      },
      {
        field: 'description',
        headerName: 'Description',
        hide: false,
        index: 7,
        width: 200,
        type: 'string',
        sortable: false,
      },
      {
        field: 'bankMemo',
        headerName: 'Bank Memo',
        hide: false,
        index: 8,
        width: 200,
        type: 'string',
        sortable: false,
      },
      {
        field: 'account',
        headerName: 'Account',
        hide: false,
        index: 9,
        width: 200,
        type: 'string',
        sortable: true,
        inlineFilter: true,
        inlineFilterName: BankFeedsFilter.Account,
        inlineFilterIDField: 'id',
        inlineFilterLabelField: 'name',
        // inlineFilterOptions: accountFilterList,
        // inlineFilterSelected: selectedAccountsList,
      },
      {
        field: 'ruleApplied',
        headerName: 'Rule Applied',
        hide: false,
        index: 10,
        width: 200,
        type: 'string',
        sortable: true,
        inlineFilter: true,
        inlineFilterName: BankFeedsFilter.RuleApplied,
        inlineFilterIDField: 'id',
        inlineFilterLabelField: 'name',
        // inlineFilterOptions: bankRulesFilterList,
        // inlineFilterSelected: selectedBankRulesList,
      },
      {
        field: 'score',
        headerName: 'Score',
        hide: false,
        index: 11,
        width: 100,
        type: 'number',
        customType: CustomType.Percent,
        decimalPlaces: 0,
        sortable: false,
        //! NO FILTER & NO SORT
        renderCell: ({ value }) => {
          return (
            <Chip
              sx={{
                '& .MuiChip-label': {
                  fontSize: '1rem',
                  lineHeight: '1.5',
                },
                backgroundColor: '#DEF2E4',
              }}
              label={value}
            />
          );
        },
      },
      {
        field: 'capCallReceived',
        headerName: 'Capital Call Received',
        hide: false,
        index: 12,
        width: 200,
        type: 'string',
        sortable: false,
        //? HOW DO WE KNOW OR HANDLE THIS ONE?
      },

      {
        field: 'action',
        headerName: 'Columns',
        hide: false,
        hideable: false,
        index: 13,
        type: 'action',
        sortable: false,
        filterable: false,
        maxWidth: 100,
        customType: CustomType.Action,
        disableColumnMenu: true,
        disableReorder: true,
        disableExport: true,
      },
    ];

    let sortedHeaders: any;

    if (bankFeedsColumnOrder && bankFeedsColumnOrder.viewItems) {
      bankFeedsColumnOrder.viewItems.map((item) => {
        const header = updatedHeaders.find(
          (header: any) => header.field === item.code
        );

        if (header) {
          header.index = item.order;
          header.hide = !item.visible;
        }
      });

      sortedHeaders = updatedHeaders.sort((a: any, b: any) =>
        a.index > b.index ? 1 : -1
      );
    } else {
      sortedHeaders = updatedHeaders.sort(
        (item1: any, item2: any) => item1.index - item2.index
      );
    }

    setHeaderList(updatedHeaders);
  };

  const handleFilter = (filterName: BankFeedsFilter, selected: string[]) => {
    setHeaderList((prevHeaderList) =>
      prevHeaderList?.map((header) => {
        if (header.inlineFilterName === filterName) {
          return {
            ...header,
            inlineFilterSelected: selected.length
              ? selected
              : header.inlineFilterOptions?.map((option) => option.id),
          };
        }
        return header;
      })
    );
  };

  const handleNewButtonAction = (actionId: string, event: any) => {
    //TBD type
    switch (actionId) {
      case AddNewButtonOptions.LinkAccount:
        setShowBankConnectionPanel(true);
        break;

      case AddNewButtonOptions.UploadBankFeedTemplate:
        const file = event?.target?.files?.[0];

        if (file) {
          //setUploadedFile(file);
        }
        break;

      case AddNewButtonOptions.DownloadBankFeedTemplate: {
        //handleTemplateDownload();
        break;
      }
    }
  };

  const handleOnView = (
    id: string,
    bankTransaction: BankFeedTransactionType
  ) => {
    setSelectedBankTransaction({
      bankTransaction,
    });
  };

  const onDetailsClose = () => {
    setSelectedBankTransaction(undefined);
  };

  const handleUpdateHeader = (field: string) => {
    if (!headerList || headerList?.length === 0) {
      return;
    }

    const activeFields = headerList.filter(
      (header) => !header.hide && header?.type !== 'action'
    );
    const targetField = headerList.find((header) => header.field === field);

    if (activeFields.length <= 1 && !targetField?.hide) {
      return;
    }

    const updatedHeaders: Array<DataGridColDef> = headerList.map((header) => {
      return {
        ...header,
        hide: header.field === field ? !header.hide : header.hide,
      };
    });

    if (updatedHeaders) {
      setHeaderList(updatedHeaders);
      const activeHeaders = headerList.filter((header) => !header.hide);

      setActiveHeaderFields(activeHeaders.length - 1);
    }

    const visiblityUpdate = arrayVisibilityUpdate(
      headerList,
      clientId,
      BANK_FEEDS_VIEW_KEY,
      field
    );

    saveColumnOrder(visiblityUpdate);
  };

  const onColumnOrderChange = (params: GridColumnOrderChangeParams) => {
    const newIndex = params.targetIndex;
    const oldIndex = params.oldIndex;

    const columnOrderToSave = arrayIndexUpdate(
      headerList,
      oldIndex - 1,
      newIndex - 1,
      clientId,
      BANK_FEEDS_VIEW_KEY
    );

    saveColumnOrder(columnOrderToSave);
  };

  const onBankConnectionClose = () => {
    setShowBankConnectionPanel(false);
  };

  return {
    viewState,
    setViewState,
    handleNewButtonAction,
    showBankConnectionPanel,
    onBankConnectionClose,
    isLoading,
    headerList,
    activeHeaderFields,
    handleUpdateHeader,
    onColumnOrderChange,
    bankFeedsSelectionModel,
    setBankFeedsSelectionModel,
    handleFilter,
    handleOnView,
    onDetailsClose,
  };
};
